import { ReactText } from "react";
import { RouteComponentProps } from "react-router-dom";
import { ua } from '../ua';
import { weappSDK } from "../sdk";
import { AnyObj, ModulePath, ModuleSign, ModuleSignMap, MoudleDialogType, MoudleDialogOptions, ModuleDataType } from './types';
import defaultModulePath, { associatedBrowserConfig, moduleSignToModule } from "./routes";
import RootPathMap from "./RootPathMap";
import { baseConfigInstance, IConfigKey } from "../baseConfig";
import { moduleRouterConfig } from './moduleRouter';

export const IDPREFIX = 'id';

export const SEPARATOR = '__';

export const IDKEY = IDPREFIX;

function toCapitalizeCase (name: string) {
  // 将所有字符变成小写
  name = name.toLowerCase();
 
  // 提取首字母转换为大写并与其余的字母拼接起来
  name = name.charAt(0).toUpperCase() + name.slice(1);

  return name;
}

/**
 * 提供了跨模块路由相关的工具，主要配合CorsRouter组件场景使用
 */
class ModuleRoutes {



  /**
   * 路由表
   */
  private paths: ModulePath[] = [];

  /**
   * 根路由表
   */
  // private rootPaths: any = {};

  /**
   * 模块路由信息配置表
   */
  private modules: ModuleSignMap = {};

  /**
   * 路由中id的参数名称
   */
  public idKey: string = IDKEY;

  /**
   * ID和params之间的分隔符
   */
  public paramSeparator: string = SEPARATOR;

  public rootPathHander: RootPathMap;

  // public idPrefix: string = IDPREFIX;

  // public idRegExp: string = `(\\\/[^\\\/]+)(\\\/${IDPREFIX}[^\\\/]+)`;

  /**
   * 实例化路由工具，可以预传入路由表
   * @param paths 
   */
  constructor(paths?: ModulePath[]) {
    this.rootPathHander = new RootPathMap();
    this.paths = (paths || []).map(path => ({ ...path, routePath: this.generateRoute(path) }));
  }

  /**
   * 模块标示新老module转换
   * @param module 旧模块标示
   * @returns 新模块标示
   */
  getNewModule = (module: string) => {
    if (module in associatedBrowserConfig) {
      return associatedBrowserConfig[module]?.module || module;
    }
    return module;
  }

  /**
   * 获取paths列表
   * @returns paths列表
   */
  getPaths = (): ModulePath[] => {
    return this.paths;
  }

  /**
   * 检查该路由是否在路由表中
   * @param path 被检查的路由
   * @returns 路由是否存在表中（true: 存在; false: 不存在）
   */
  checkPath = (path: ModulePath | string): boolean => {
    return this.paths.findIndex(p => this.generateRoute(p) === this.generateRoute(path)) !== -1;
  }

  /**
   * 注册单个模块配置
   * @param key 
   * @param module 
   * @returns 
   */
  registerModule = (key: string, module: ModuleSign): boolean => {
    /* 注册模块 */
    if (module) {
      this.modules[key] = module;
      /* 注册模块的路由表 */
      if (module.paths) {
        module.paths.forEach(moduleRouter.registerPath);
      }
      return true;
    }
    return false;
  }

  /**
   * 批量注册多个模块配置
   * @param moduleObj 
   * @returns 
   */
  registerModules = (moduleObj: ModuleSignMap) => {
    if (!moduleObj) return;
    /* 处理特殊处理的路由 */
    Object.keys(moduleObj).forEach(module => {
      const modulePaths = moduleObj[module];
      this.registerModule(module, modulePaths);
    });
  }

  /**
   * 将路由加入到paths列表中
   * @param path 需要注册的路由
   * @returns 路由是否注册成功（true: 注册成功; false: 注册失败）
   */
  registerPath = (path: ModulePath): boolean => {
    // 需要对传入的path进行校验，比对是否可以加入到路由表中
    if (!this.checkPath(path)) {
      this.paths.push({ ...path, routePath: this.generateRoute(path) });
      return true;
    }
    return false;
  }

  /**
   * 根据模块名称生成对应的模块路由
   * @param module 模块标识
   * @param type 弹框类型
   * @returns 生成的模块路由
   */
  generateMoudelPath = (module: string, type: MoudleDialogType = 'default') => {
    const modulePaths = module in this.modules ? this.modules[module].paths : undefined;
    let path: ModulePath;
    if (modulePaths) {
      path = modulePaths[type === 'side' ? 1 : 0];
    } else {
      path = { app: `@weapp/${module}`, compName: `${toCapitalizeCase(module)}View${type === 'side' ? 'Side' : ''}Dialog` };
    }
    path.routePath = this.generateRoute(path);
    return path;
  }

  /**
   * 根据app和compName生成路由
   * @param path 路由配置信息
   * @returns 生成的路由
   */
  generateRoute = (path: ModulePath | string): string => {
    if (!path || typeof path === 'string') return path || '';
    return path.routePath || `/${`${path.app.replace('@weapp/', '')}_${path.compName}`.replace('/', '_')}`;
  }

  /**
   * 获取父级路由
   * @param props 当前组件的props
   * @returns 父级路由
   */
  formatParentPath = (props: RouteComponentProps) => {
    return props.match.url.replace(/\/$/, '');
  }

  // getId = (props: any) => {
  //   const patt = new RegExp(this.idRegExp, 'g');
  //   const { location } = props;
  //   const paths = location?.pathname?.match(patt);
  //   if (paths && paths.length) {
  //     patt.test(paths[paths.length - 1]);
  //     return RegExp.$2 ? RegExp.$2.replace(`\/${this.idPrefix}`, '') : '';
  //   }
  //   return '';
  // }

  formatId = (idStr: string) => {
    let id = idStr;
    let params = {};
    if (idStr && idStr.indexOf(this.paramSeparator) !== -1) {
      id = idStr.substr(0, idStr.indexOf(this.paramSeparator));
    }
    if (idStr && idStr.indexOf(this.paramSeparator) !== -1) {
      params = this.stringToParams(idStr.substr(idStr.indexOf(this.paramSeparator) + this.paramSeparator.length));
    }
    return { id, params };
  }

  /**
   * 将配置文件数据转换成formatModuleSign函数需要的格式进行数据处理
   * @param moduleDatas  
   */
  formatModuleSignMap = (moduleDatas: string[][]): ModuleSignMap => {
    if (moduleDatas && Array.isArray(moduleDatas) && moduleDatas.length) {
      const moduleDatasMap: any[] = moduleDatas.map(item=>{
        const [ moduleName, oldModuleName, registry, mAddress, mDetailAddress, detailAddress, AddDialogAddress, dialogAddress, slidedDialogAddress, associatedButton ] = item;
        return {
          moduleName,
          oldModuleName,
          registry,
          mAddress,
          mDetailAddress,
          detailAddress,
          AddDialogAddress,
          dialogAddress,
          slidedDialogAddress,
          associatedButton
        }
      })
      return this.formatModuleSign(moduleDatasMap)
    } 
    return {}
  }

  /**
   * 将接口返回的数据转化为模块标识数据
   * @param moduleDatas 
   */
  formatModuleSign = (moduleDatas: ModuleDataType[]): ModuleSignMap => {
    const modules: ModuleSignMap = {};
    try {
      if (moduleDatas && Array.isArray(moduleDatas) && moduleDatas.length) {
        for (let i = 0; i < moduleDatas.length; i ++) {
          const moduleData = moduleDatas[i];
          if (moduleData && moduleData.moduleName) {
            const { moduleName, oldModuleName, registry, mAddress, mDetailAddress, detailAddress, AddDialogAddress, dialogAddress, slidedDialogAddress, associatedButton } = moduleData;
            if (registry && dialogAddress) {
              const paths: ModulePath[] = [
                { app: registry, compName: dialogAddress },
                { app: registry, compName: slidedDialogAddress || dialogAddress },
              ];
              const moduleSignData: ModuleSign = {
                module: moduleName,
                mListPath: mAddress || '',
                mDetailPath: mDetailAddress || '',
                detailPath: detailAddress || '',
                paths,
              };
              if (AddDialogAddress) {
                moduleSignData.addPaths = [
                  { app: registry, compName: AddDialogAddress },
                ];
              }
              if (associatedButton) {
                const browsers = associatedButton.split(',');
                moduleSignData.type = browsers.length === 1 ? browsers[0] : browsers;
              }
              modules[oldModuleName] = moduleSignData;
            }
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
    return modules;
  }

  /**
   * 获取路由上的id参数
   * @param props 当前组件的props，并且组件有被withRouter方法包裹过（参数是用match属性中获取到的）
   * @returns 路由上的id的值
   */
  getId = (props: RouteComponentProps) => {
    const { match } = props;
    let idStr = (match.params as any)[this.idKey];
    // if (id && id.indexOf(this.paramSeparator) !== -1) {
    //   id = id.substr(0, id.indexOf(this.paramSeparator));
    // }
    // return id;
    return this.formatId(idStr).id;
  }

  /**
   * 获取id中附加的参数
   * @param props 当前组件的props，并且组件有被withRouter方法包裹过（参数是用match属性中获取到的）
   * @returns 附加参数
   */
  getParams = (props: RouteComponentProps): AnyObj => {
    const { match } = props;
    let idStr = (match.params as any)[this.idKey];
    // let params: AnyObj = {};
    // if (idStr && idStr.indexOf(this.paramSeparator) !== -1) {
    //   params = this.stringToParams(idStr.substr(idStr.indexOf(this.paramSeparator) + this.paramSeparator.length));
    // }
    // return params;
    return this.formatId(idStr).params;
  }

  getModule = (module: string) => {
    return this.getModulePath(module);
  }

  getModules = () => {
    return this.modules;
  }

  getModulePath = (module: string): ModuleSign | undefined => {
    return this.modules[module];
  }

  /**
   * 将对象转换成URL中的字符串
   * @param params 参数对象
   * @returns 转化后的字符串
   */
  paramsToString = (params: AnyObj): string => {
    return Object.keys(params).reduce((prevValue, key) => {
      const value = params[key];
      if (typeof value === 'function') {
        return prevValue;
      }
      let valueStr = `${key}=${encodeURIComponent(value === undefined || value === null ? '' : value)}`;
      if (prevValue) {
        valueStr = `${prevValue}&${valueStr}`;
      }
      return valueStr;
    }, '');
  };

  /**
   * 将字符串转换成对象
   * @param paramsStr 需要转换的字符串
   * @returns 转换得到的对象
   */
  stringToParams = (paramsStr: string): AnyObj => {
    const params: AnyObj = {};
    String(paramsStr).split('&').forEach(item => {
      const [key, value] = item.split('=');
      params[key] = decodeURIComponent(value);
    });
    return params;
  }

  /**
   * id和params生成id字符串
   * @param id 
   * @param options 
   * @returns 
   */
  generateId = (id?: ReactText, options?: MoudleDialogOptions): string => {
    const idInvalid = id === '' || id === undefined || id === null;

    const paramStr = options?.params ? this.paramsToString(options?.params) : '';

    return idInvalid ? '' : `${id}${paramStr ? `${this.paramSeparator}${paramStr}` : ''}`;
  }

  /**
   * 根据props中的weId来生成子路由
   * @param props 当前组件的props
   * @param path 子路由
   * @returns 拼接好父级的子路由
   */
  generateSubPath = (props: RouteComponentProps, path: ModulePath | string, id?: ReactText, options?: MoudleDialogOptions) => {

    const weId: string = (props as any).weId || '';

    let parentPath = options?.rootPath !== undefined ? options.rootPath : this.formatParentPath(props);

    const matchedRootPath = this.rootPathHander.getRootPathByWeId(weId);

    if (matchedRootPath) {
      parentPath = matchedRootPath;
    }

    const routePath = this.generateRoute(path);

    // 处理路由中的id
    let idStr = this.generateId(id, options);

    if (idStr) {
      idStr = `/${idStr}`;
    }

    // 如果匹配到了，则基于对应的根路由生成子路由；如果没有匹配到，则基于当前路由生成子路由
    return `${parentPath}${routePath}${idStr}`;
  }

  getMoudleDialogType = (
    configValue: any = {},
    options: MoudleDialogOptions | MoudleDialogType | undefined,
    module: string
  ): MoudleDialogOptions | MoudleDialogType => {
    let type: MoudleDialogType = configValue[IConfigKey.commonOpenmethod] === 'newPage' ? 'link' : 'default';

    // 判断是否有对应地址 无对应地址 直接设置为默认效果
    if (type === 'link') {
      const modulePath = this.getModulePath(module);
      if (modulePath) {
        const { detailPath, mDetailPath } = modulePath;
        const isMobile = (typeof options === 'object' && typeof options.isMobile !== undefined) ? options.isMobile : ua.device === 'Mobile';
        let singlePath = (isMobile ? mDetailPath : detailPath) || '';
        if (!singlePath) {
          type = 'default'
        }
      } else {
        type = 'default'
      }
    } 


    if (typeof options === 'object' && !options.type ) {
      options.type = type;
    } else if(!options) {
      options = type;
    }
    return options;
  };

  generateType = (
    configValue: any = {},
    defaultType: MoudleDialogType,
    module: string,
  ) => {
    let type: MoudleDialogType = configValue[IConfigKey.commonOpenmethod] === 'newPage' ? 'link' : defaultType;

    // 配合兼容 如果打开方式为link的情况下  支持单页效果
    if (type === 'link') {
      const modulePath = this.getModulePath(module);
      if (modulePath) {
        const { detailPath, mDetailPath } = modulePath;
        const isMobile = ua.device === 'Mobile';
        let singlePath = (isMobile ? mDetailPath : detailPath) || '';
        if (!singlePath) {
          type = defaultType
        }
      } else {
        type = defaultType
      }
    } 
    return type;
  }

  open = (
    props: RouteComponentProps,
    module: string,
    id: ReactText,
    options?: MoudleDialogOptions | MoudleDialogType
  ) => {
    if (window.weaverSDK?.isExternSystem?.()) {
      // 如果在外部系统中，强制使用 link 单页打开形式
      options = typeof options === 'object' ? options : {} || {};
      options.type = 'link';
    }
    // 特殊兼容my关注模块  传递特殊参数[defaultType]的时候 支持控制 弹窗 跟 新页面.
    if (typeof options ==='object' && options.defaultType) {
      baseConfigInstance.ready((config: any) => {
        // @ts-ignore 
        options.type = this.generateType(config?.configValue, options.defaultType, module);
        this.originOpen(props, module, id, options);
      });
      return true;
    }

    // 默认有值的情况下  直接调用方法
    if ((typeof options ==='object' && options.type) || (typeof options ==='string' && options)) {
      return this.originOpen(props, module, id, options);
    }

    // 没有值的情况下走判断 
    if (!baseConfigInstance.configIsReady) {
      if (baseConfigInstance.configValue) {
        options = this.getMoudleDialogType(baseConfigInstance.configValue, options, module);
      }
      return this.originOpen(props, module, id, options);
    } else {
      baseConfigInstance.ready((config: any) => {
        options = this.getMoudleDialogType(config?.configValue, options, module);
        this.originOpen(props, module, id, options);
      });
    }
  };

  /**
   * 打开事项卡片弹框
   * @param props 当前组件的路由
   * @param module 模块id标示
   * @param id 传给事项卡片的id标识
   * @param type 弹框类型（default: 中间打开弹框, side: 侧边打开弹框）
   * @returns 是否能够跳转路由
   */
  originOpen = (props: RouteComponentProps, module: string, id: ReactText, options: MoudleDialogOptions | MoudleDialogType = 'default') => {
    let path: ModulePath;
    if (window.weaverSDK?.isExternSystem?.()) {
      if (!(typeof options === 'object' && options.type === 'link')) {
        // 如果在外部系统中，并且打开方式不是单页打开，拦截动作并给出提示
        window.weappUi?.Dialog?.message?.({
          type: 'info',
          content: '抱歉，该功能需要访问OA系统使用，带来不便，请您谅解',
        });
        return;
      }
    }
    if (options === 'link' || (typeof options ==='object' && options.type === 'link')) {
      const modulePath = this.getModulePath(module);
      if (modulePath) {
        const { detailPath, mDetailPath } = modulePath;
        const isMobile = (typeof options === 'object' && typeof options.isMobile !== undefined) ? options.isMobile : ua.device === 'Mobile';
        let singlePath = (isMobile ? mDetailPath : detailPath) || '';
        singlePath = singlePath.replace('{id}', typeof options === 'object' ? this.generateId(id, options) : id as string);
        // props.history.push(singlePath);
        if (window.weaverSDK?.isExternSystem?.()) {
          // 三方系统场景下，需要拼接 OA 的域名地址，再跳转
          singlePath = `${window.weaverSDK?.getOADomain?.() || ''}${singlePath}`;
        }
        weappSDK.openLink({ url: singlePath });
      }
      return;
    }
    if (typeof options === 'string') {
      path = this.generateMoudelPath(module, options);
    } else {
      path = this.generateMoudelPath(module, options?.type || 'default')
    }
    if (!this.checkPath(path)) return false;
    let subRoute;
    if (typeof options === 'string') {
      subRoute = this.generateSubPath(props, path, id);
    } else {
      subRoute = this.generateSubPath(props, path, id, options?.rootPath !== undefined ? options : undefined);
    }
    try {
      props.history.push(subRoute);
    } catch (e) {
      return false;
    }
    return true;
  }
}

/* 创建唯一实例，只导出唯一实例 */
const moduleRouter = new ModuleRoutes(defaultModulePath);

/* 处理特殊处理的路由 */
moduleRouter.registerModules(moduleSignToModule);

try {
  moduleRouter.registerModules(moduleRouter.formatModuleSignMap(moduleRouterConfig));
} catch (error) {
  console?.error?.(error);
}
// Object.keys(moduleSignToModule).forEach(module => {
//   const modulePaths = moduleSignToModule[module];
//   /* 注册模块 */
//   moduleRouter.registerModule(module, modulePaths);
//   /* 注册模块的路由表 */
//   if (modulePaths && modulePaths.paths) {
//     modulePaths.paths.forEach(moduleRouter.registerPath);
//   }
// });

export default moduleRouter;
